import { useEffect, useMemo, type Dispatch, type SetStateAction } from "react";
import type { TFunction } from "i18next";

import type { TransportLeg } from "src/api/client-api/transport/useTransportLegs";
import type { TransportCircuit } from "src/api/client-api/transport/useTransportCircuits";
import { getTransportCircuitEndpoints, type TransportRouteEndpoints } from "src/utils/transport/resourceMeta";
import type { TransportResourceFilter } from "./transportOverviewState";

const TRANSPORT_OVERVIEW_PAGE_SIZE = 6;

export type TransportOverviewItem =
    | {
          id: string;
          kind: "leg";
          title: string;
          route: TransportRouteEndpoints;
          searchText: string;
          leg: TransportLeg;
      }
    | {
          id: string;
          kind: "circuit";
          title: string;
          route: TransportRouteEndpoints;
          searchText: string;
          circuit: TransportCircuit;
      };

type UseTransportOverviewResourcesParams = {
    t: TFunction;
    transportLegs: TransportLeg[];
    transportCircuits: TransportCircuit[];
    search: string;
    filter: TransportResourceFilter;
    page: number;
    setPage: Dispatch<SetStateAction<number>>;
    selectedIds: string[];
    setSelectedIds: Dispatch<SetStateAction<string[]>>;
};

const hasLegCoords = (leg: TransportLeg) =>
    leg.originLatitude != null &&
    leg.originLongitude != null &&
    leg.destinationLatitude != null &&
    leg.destinationLongitude != null;

const circuitHasCoords = (circuit: TransportCircuit) =>
    circuit.circuitLegs.some((circuitLeg) => {
        const leg = circuitLeg.transportLeg;
        return typeof leg === "object" && leg !== null && hasLegCoords(leg);
    });

const isItemMappable = (item: TransportOverviewItem) =>
    item.kind === "leg" ? hasLegCoords(item.leg) : circuitHasCoords(item.circuit);

const matchesFilter = (item: TransportOverviewItem, filter: TransportResourceFilter) => {
    if (filter === "all") return true;
    if (filter === "legs") return item.kind === "leg";
    return item.kind === "circuit";
};

export const useTransportOverviewResources = ({
    t,
    transportLegs,
    transportCircuits,
    search,
    filter,
    page,
    setPage,
    selectedIds,
    setSelectedIds,
}: UseTransportOverviewResourcesParams) => {
    const items = useMemo<TransportOverviewItem[]>(() => {
        const legs: TransportOverviewItem[] = transportLegs.map((leg) => {
            const title = leg.name ?? `${t("transport.resource.kind.leg", "Leg")} #${leg.id}`;
            const origin = leg.originLabel ?? leg.originCountryCode ?? "-";
            const destination = leg.destinationLabel ?? leg.destinationCountryCode ?? "-";

            return {
                id: `leg-${leg.id}`,
                kind: "leg",
                title,
                route: { origin, destination },
                searchText: `${title} ${origin} ${destination} ${leg.linkMode}`.toLowerCase(),
                leg,
            };
        });

        const circuits: TransportOverviewItem[] = transportCircuits.map((circuit) => {
            const title = circuit.name ?? `Circuit #${circuit.id}`;
            const route = getTransportCircuitEndpoints(circuit);

            return {
                id: `circuit-${circuit.id}`,
                kind: "circuit",
                title,
                route,
                searchText: `${title} ${route.origin} ${route.destination} ${circuit.linkMode}`.toLowerCase(),
                circuit,
            };
        });

        return [...legs, ...circuits].toSorted((a, b) => a.title.localeCompare(b.title));
    }, [t, transportCircuits, transportLegs]);

    const visibleItems = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();

        return items.filter((item) => {
            const matchesSearch = normalizedSearch === "" || item.searchText.includes(normalizedSearch);
            return matchesFilter(item, filter) && matchesSearch;
        });
    }, [filter, items, search]);

    const totalPages = Math.max(1, Math.ceil(visibleItems.length / TRANSPORT_OVERVIEW_PAGE_SIZE));
    const paginationPages = useMemo(
        () => Array.from({ length: totalPages }, (_, index) => ({ label: String(index + 1) })),
        [totalPages],
    );
    const paginatedItems = useMemo(() => {
        const startIndex = (page - 1) * TRANSPORT_OVERVIEW_PAGE_SIZE;
        return visibleItems.slice(startIndex, startIndex + TRANSPORT_OVERVIEW_PAGE_SIZE);
    }, [page, visibleItems]);

    useEffect(() => {
        setPage(1);
    }, [filter, search, setPage]);

    // Pagination lives in the layout context and must be clamped when tenant-filtered resources shrink.
    useEffect(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (page > totalPages) {
            // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
            setPage(totalPages);
        }
    }, [page, setPage, totalPages]);

    // Selection also lives in the layout context; prune ids that do not exist for the active tenant.
    useEffect(() => {
        const itemIds = new Set(items.map((item) => item.id));
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
        setSelectedIds((current) => {
            const next = current.filter((selectedId) => itemIds.has(selectedId));
            return next.length === current.length ? current : next;
        });
    }, [items, setSelectedIds]);

    const selectedItems = useMemo(() => items.filter((item) => selectedIds.includes(item.id)), [items, selectedIds]);
    const selectedLegs = selectedItems.flatMap((item) => (item.kind === "leg" ? [item.leg] : []));
    const selectedCircuits = selectedItems.flatMap((item) => (item.kind === "circuit" ? [item.circuit] : []));
    const mappableItemIds = useMemo(() => items.filter(isItemMappable).map((item) => item.id), [items]);
    const filterButtons = useMemo<Array<{ id: TransportResourceFilter; label: string }>>(
        () => [
            {
                id: "all",
                label: t("transport.overview.filter.all", "All ({{count}})", { count: items.length }),
            },
            {
                id: "legs",
                label: t("transport.overview.filter.legs", "Legs ({{count}})", { count: transportLegs.length }),
            },
            {
                id: "circuits",
                label: t("transport.overview.filter.itineraries", "Itineraries ({{count}})", {
                    count: transportCircuits.length,
                }),
            },
        ],
        [items.length, t, transportCircuits.length, transportLegs.length],
    );

    return {
        filterButtons,
        items,
        mappableItemIds,
        paginatedItems,
        paginationPages,
        selectedCircuits,
        selectedLegs,
        totalPages,
        visibleItems,
    };
};
